// DN detail «Түүх» tab: audit journal (GET /ord/delivery_management/:id/audit-log) and «Тэмдэглэл нэмэх»
// (POST .../note, ORD_DN_MNG). The add-note button is always shown; without the permission it is
// aria-disabled with the reason (O94).

import { useState } from "react"
import { Box, Button, Group, Loader, Modal, Stack, Text, Textarea, Timeline } from "@mantine/core"
import { ChatCenteredTextIcon, CheckCircleIcon, ClockClockwiseIcon, PackageIcon, WarningCircleIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { HelpIcon } from "@dehub/theme"
import useSWR from "swr"
import { DeliveryApi } from "../../apis"
import type { IDeliveryAuditLogEntry } from "../../apis/delivery"
import { dateTimeSecFormat } from "../../utils/date"
import { message } from "../../utils/message"
import { DeliveryActionButton } from "../delivery/action-controls"
import type { DeliveryActionState } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"
import classes from "./dn-detail.module.css"

type Props = {
  deliveryNoteId: string
  addNoteState: Pick<DeliveryActionState, "allowed" | "reason">
}

const NOTE_MAX_LENGTH = 2000

function iconForAction(actionType: string) {
  switch (actionType) {
    case "DELIVERED":
    case "POD_VERIFIED":
    case "OTP_VERIFIED":
      return <CheckCircleIcon size={14} />
    case "FAILED":
    case "EXCEPTION_RAISED":
    case "POD_REJECTED":
    case "RETURNED":
      return <WarningCircleIcon size={14} />
    case "NOTE_ADDED":
    case "DELIVERY_PROGRESS_NOTE":
      return <ChatCenteredTextIcon size={14} />
    case "ASSIGNED":
    case "REASSIGNED":
    case "PICKED_UP":
    case "IN_TRANSIT":
    case "OUT_FOR_DELIVERY":
      return <PackageIcon size={14} />
    default:
      return <ClockClockwiseIcon size={14} />
  }
}

export function DnAuditLogPanel({ deliveryNoteId, addNoteState }: Props) {
  const { t } = useTranslation(["delivery", "common"])
  const [noteOpen, setNoteOpen] = useState(false)
  const [noteText, setNoteText] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const { data, isLoading, mutate } = useSWR(
    deliveryNoteId ? `/ord/delivery_management/${deliveryNoteId}/audit-log` : null,
    () => DeliveryApi.auditLog(deliveryNoteId),
    { revalidateOnFocus: false },
  )

  const rows: IDeliveryAuditLogEntry[] = data?.rows ?? []

  const onSubmitNote = async () => {
    const trimmed = noteText.trim()
    if (!trimmed) {
      message.error(t("delivery.audit.noteEmpty"))
      return
    }
    setSubmitting(true)
    try {
      await DeliveryApi.addNote(deliveryNoteId, trimmed)
      setNoteText("")
      setNoteOpen(false)
      await mutate()
      message.success(t("common:messages.requestSuccess"))
    } catch (err) {
      message.error(translateBackendError(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box className={classes.attachRow}>
      <Group justify="space-between" mb="md">
        <Text className={classes.sectionTitle}>
          {t("delivery.audit.title")}
          <HelpIcon helpKey="order.supplier-delivery-detail.section.audit-log" variant="section" />
        </Text>
        <DeliveryActionButton state={addNoteState} variant="default" leftSection={<ChatCenteredTextIcon size={16} />} onClick={() => setNoteOpen(true)}>
          {t("delivery.audit.addNote")}
        </DeliveryActionButton>
      </Group>

      {isLoading ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      ) : rows.length === 0 ? (
        <Text className={classes.hint} ta="center" py="md">
          {t("delivery.audit.empty")}
        </Text>
      ) : (
        <Timeline active={rows.length} bulletSize={24} lineWidth={2} color="gray">
          {rows.map((row) => (
            <Timeline.Item
              key={row.id}
              bullet={iconForAction(row.actionType)}
              title={
                <Group gap="sm" wrap="wrap">
                  <Text fw={650} fz="var(--nw-fz-body)">
                    {t(`delivery.audit.actions.${row.actionType}`, { defaultValue: t("delivery.audit.actions.OTHER") })}
                  </Text>
                  <Text className={classes.label}>{dateTimeSecFormat(row.occurredAt)}</Text>
                </Group>
              }
            >
              {row.notes && (
                <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" mt={2} className={classes.preWrap}>
                  {translateBackendError(row.notes)}
                </Text>
              )}
            </Timeline.Item>
          ))}
        </Timeline>
      )}

      <Modal opened={noteOpen} onClose={() => setNoteOpen(false)} title={t("delivery.audit.addNote")} centered size="md">
        <Stack gap="sm">
          <Textarea
            value={noteText}
            onChange={(e) => setNoteText(e.currentTarget.value)}
            placeholder={t("delivery.audit.notePlaceholder")}
            minRows={4}
            maxLength={NOTE_MAX_LENGTH}
            autosize
            description={`${noteText.length} / ${NOTE_MAX_LENGTH}`}
            inputWrapperOrder={["label", "input", "description", "error"]}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setNoteOpen(false)} disabled={submitting}>
              {t("common:actions.dismiss")}
            </Button>
            <Button onClick={onSubmitNote} loading={submitting}>
              {t("common:actions.save")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}
